import type { BoxProps, FlexProps, TextProps } from '@vimeo/bokeh';
import { Box, Flex, Text, rem } from '@vimeo/bokeh';

import { SCROLLBAR_STYLE_SX } from '../../constants';

const listItemHeight = 56;

export const Divider = () => <Box w="100%" h="0.5pt" backgroundColor="stroke" />;

export const PaddedDivider = () => (
  <Box px="16px" w="100%">
    <Divider />
  </Box>
);

export const Section = ({ children, ...props }: FlexProps) => (
  <Flex
    position="relative"
    flexDirection="column"
    alignItems="flex-start"
    gap="8px"
    alignSelf="stretch"
    padding="12px 16px"
    {...props}
  >
    {children}
  </Flex>
);

export const SectionTitle = ({ children, ...props }: FlexProps) => (
  <Flex
    display="flex"
    padding="2px 0"
    justifyContent="space-between"
    alignItems="center"
    alignSelf="stretch"
    {...props}
  >
    {children}
  </Flex>
);

export const SectionLabel = ({ children, ...props }: TextProps) => (
  <Text color="text-secondary" variant={'body-sm'} {...props}>
    {children}
  </Text>
);

export const ScrollableSection = ({ children, ...props }: BoxProps) => (
  <Section {...props} height="100%" overflowY="auto" sx={SCROLLBAR_STYLE_SX}>
    {children}
  </Section>
);

export const Thumbnail = (props: any) => (
  <Box
    as="img"
    width="100%"
    sx={{
      aspectRatio: '85 / 48',
      backgroundColor: 'black',
      objectFit: 'contain',
    }}
    {...props}
  />
);

export const ThumbnailContainer = ({ children, ...props }: BoxProps) => (
  <Box
    width={rem(83)}
    flexShrink={0}
    position="relative"
    borderRadius="8px"
    overflow="hidden"
    border="0.5pt solid"
    borderColor="stroke"
    {...props}
  >
    {children}
  </Box>
);

export const ThumbnailContainerOverlay = ({ children, ...props }: BoxProps) => (
  <Box
    className="thumbnailOverlay"
    width="100%"
    height="100%"
    opacity={0}
    position="absolute"
    backgroundColor="rgba(0, 0, 0, 0.5)"
    display="grid"
    placeItems="center"
    {...props}
  >
    {children}
  </Box>
);

export const ThumbnailDetails = ({ children, ...props }: BoxProps) => (
  <Box display="flex" alignItems="center" gap="8px" flexGrow={1} {...props}>
    {children}
  </Box>
);

export const ThumbnailTitle = ({ children, ...props }: TextProps) => (
  <Text
    color="text-primary"
    fontSize="14px"
    fontWeight="700"
    flex="1"
    noOfLines={3}
    variant="body-xl"
    {...props}
  >
    {children}
  </Text>
);

export const ThumbnailTimeCode = ({ children, ...props }: TextProps) => {
  return (
    <Text
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="flex-end"
      gap="10px"
      height="20px"
      padding="4px"
      alignSelf="center"
      borderRadius="6px"
      backgroundColor={'fill-blur'}
      color="text-primary"
      textAlign="center"
      fontSize="12px"
      fontStyle="normal"
      variant="body-xl"
      {...props}
    >
      {children}
    </Text>
  );
};

export const ListItem = ({ children, ...props }: BoxProps) => {
  return (
    <Flex
      height={listItemHeight}
      alignItems="center"
      gap={rem(12)}
      alignSelf="stretch"
      padding={rem(8)}
      marginX={rem(8)}
      borderRadius={'md'}
      _hover={{
        cursor: 'pointer',
        backgroundColor: 'fill-component-hover',
        '.thumbnailOverlay': {
          opacity: 1,
        },
      }}
      {...props}
    >
      {children}
    </Flex>
  );
};
